import React, { useState } from "react";
import { Check, LoaderCircle, Package, Search, Upload } from "./virtualShopIcons.jsx";
import { READ, responseValue } from "./virtualShopAdminApi.js";

const MAX_VISIBLE_ISSUES = 200;
const PAGE_SIZE = 50;

function requestKey() {
  return window.crypto?.randomUUID?.() || `vshop-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function displayKey(value) {
  return String(value || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (letter) => letter.toUpperCase());
}

function printable(value, fallback = "—") {
  if (value === null || value === undefined || value === "") return fallback;
  if (["string", "number", "boolean"].includes(typeof value)) return String(value);
  return fallback;
}

function formatDate(value) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? new Intl.DateTimeFormat("en-AU", { dateStyle: "medium", timeStyle: "short" }).format(date)
    : printable(value);
}

function fileIdentity(file) {
  return file ? `${file.name}:${file.size}:${file.lastModified || 0}` : null;
}

function resultMessage(result, fallback) {
  return result?.message || (result?.reason === "invalid-import" ? "Choose a non-empty CSV file no larger than 25 MB." : fallback);
}

function MetricGrid({ values, emptyText = "No counts were returned." }) {
  const entries = Object.entries(values && typeof values === "object" ? values : {})
    .filter(([, value]) => ["number", "string"].includes(typeof value));
  if (!entries.length) return <p className="vs-ops-muted">{emptyText}</p>;
  return <div className="vs-ops-metrics">{entries.map(([key, value]) => <div key={key}><strong>{printable(value)}</strong><span>{displayKey(key)}</span></div>)}</div>;
}

function issueText(issue) {
  if (typeof issue === "string") return issue;
  return printable(issue?.message || issue?.detail || issue?.error || issue?.code, "Validation issue");
}

function IssueList({ title, issues, kind }) {
  const values = Array.isArray(issues) ? issues : [];
  if (!values.length) return null;
  const visible = values.slice(0, MAX_VISIBLE_ISSUES);
  return <section className={`vs-ops-issues is-${kind}`}>
    <header><h3>{title}</h3><span>{values.length.toLocaleString()}</span></header>
    <div className="vs-ops-issue-list">{visible.map((issue, index) => {
      const row = typeof issue === "object" ? (issue.rowNumber ?? issue.row) : null;
      const key = typeof issue === "object" ? issue.importKey : null;
      const type = typeof issue === "object" ? issue.recordType : null;
      return <article key={`${row || "issue"}-${key || index}`}><div>{row ? <span>Row {row}</span> : null}{type ? <span>{printable(type)}</span> : null}{key ? <code>{printable(key)}</code> : null}</div><p>{issueText(issue)}</p></article>;
    })}</div>
    {values.length > visible.length ? <p className="vs-ops-muted">Showing the first {visible.length.toLocaleString()} issues. Correct the file and run a new dry run for the complete result.</p> : null}
  </section>;
}

export function VirtualShopImporter({ api, onOpenSections, onOpenProducts, onOpenStorefront }) {
  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [fileIndex, setFileIndex] = useState(0);
  const [mode, setMode] = useState("create");
  const [phase, setPhase] = useState("idle");
  const [preview, setPreview] = useState(null);
  const [report, setReport] = useState(null);
  const [approved, setApproved] = useState(false);
  const [error, setError] = useState("");

  const resetResult = () => { setPreview(null); setReport(null); setApproved(false); setError(""); setPhase("idle"); };
  const chooseFile = (event) => {
    const selected = Array.from(event.target.files || []);
    setFiles(selected);
    setFileIndex(0);
    setFile(selected[0] || null);
    resetResult();
  };
  const advanceFile = () => {
    const nextIndex = fileIndex + 1;
    if (nextIndex >= files.length) return;
    setFileIndex(nextIndex);
    setFile(files[nextIndex]);
    resetResult();
  };
  const chooseMode = (next) => { if (next === mode) return; setMode(next); resetResult(); };

  async function runPreview() {
    if (!file || phase === "previewing" || phase === "committing") return;
    setError(""); setApproved(false); setPreview(null); setReport(null); setPhase("previewing");
    const result = await api.previewImport(file, mode);
    if (!result.ok) { setError(resultMessage(result, "The dry run could not be completed.")); setPhase("idle"); return; }
    const data = responseValue(result, ["preview"], result.data || {});
    const ready = data.ready === true && Boolean(data.previewId) && Boolean(data.digest)
      && data.mode === mode && data.schemaVersion === "VIRTUAL-SHOP-IMPORT-V1";
    const next = { ...data, ready, fileIdentity: fileIdentity(file), selectedMode: mode };
    setPreview(next);
    setPhase(ready ? "ready" : "blocked");
  }

  async function commit() {
    if (!approved || phase !== "ready" || !preview?.previewId || !file
      || preview.fileIdentity !== fileIdentity(file) || preview.selectedMode !== mode) return;
    setError(""); setPhase("committing");
    const result = await api.commitImport(file, { previewId: preview.previewId, mode, requestKey: requestKey() });
    if (!result.ok) { setApproved(false); setError(resultMessage(result, "The import could not be committed. No success has been assumed.")); setPhase("ready"); return; }
    setReport(responseValue(result, ["report"], result.data || {}));
    setApproved(false); setPhase("complete");
  }

  const currentPreview = preview && preview.fileIdentity === fileIdentity(file) && preview.selectedMode === mode;
  return <section className="vs-ops-screen" aria-label="Import Store Catalogue">
    <header className="vs-ops-heading"><div><span className="vs-eyebrow">OWNER-APPROVED BULK WORKFLOW</span><h2>Import Store Catalogue</h2><p>Select one or more VIRTUAL-SHOP-IMPORT-V1 section files. Each file stays below 25 MB and receives its own dry run, approval and atomic transaction.</p></div><Upload size={30} /></header>
    <div className="vs-ops-panel">
      <div className="vs-ops-step"><span>1</span><div><h3>Choose catalogue CSV files</h3><p>CSV only. Files are processed in the selected order; changing the selection invalidates every earlier dry run and approval.</p></div></div>
      <div className="vs-ops-file-row"><label className="vs-upload-button"><Upload size={17} /> Choose CSV files<input aria-label="Choose catalogue CSV" type="file" multiple accept=".csv,text/csv" onChange={chooseFile} /></label><div><strong>{file?.name || "No file selected"}</strong>{file ? <small>File {fileIndex + 1} of {files.length} · {Math.ceil(file.size / 1024).toLocaleString()} KB</small> : null}</div></div>
      <fieldset className="vs-ops-mode"><legend>Import behaviour</legend><button type="button" className={mode === "create" ? "is-active" : ""} aria-pressed={mode === "create"} onClick={() => chooseMode("create")}><strong>Create New Only</strong><span>Create missing import keys and leave every existing record unchanged.</span></button><button type="button" className={mode === "update" ? "is-active" : ""} aria-pressed={mode === "update"} onClick={() => chooseMode("update")}><strong>Update Matching</strong><span>Preview changes to matching import keys before preserving and updating their identities.</span></button></fieldset>
      <div className="vs-ops-actions"><button type="button" className="vs-primary" disabled={!file || phase === "previewing" || phase === "committing"} onClick={runPreview}>{phase === "previewing" ? <LoaderCircle size={17} /> : <Search size={17} />} {phase === "previewing" ? "Validating CSV" : "Run dry-run preview"}</button><span>No database mutation occurs during this step.</span></div>
    </div>

    {error ? <div className="vs-ops-error" role="alert">{error}</div> : null}
    {currentPreview ? <div className="vs-ops-panel" data-preview-state={preview.ready ? "ready" : "blocked"}>
      <div className="vs-ops-step"><span>2</span><div><h3>Dry-run preview</h3><p><strong>{printable(preview.schemaVersion, "Schema not reported")}</strong> · {mode === "create" ? "Create New Only" : "Update Matching"} · digest <code>{printable(preview.digest, "not reported")}</code></p></div></div>
      <div className={`vs-ops-verdict ${preview.ready === true ? "is-ready" : "is-blocked"}`}>{preview.ready === true ? <Check size={20} /> : null}<strong>{preview.ready === true ? "Ready for explicit approval" : "Import blocked — no records can be committed"}</strong></div>
      <MetricGrid values={preview.counts} />
      <IssueList title="Structural errors" issues={preview.errors} kind="error" />
      <IssueList title="Warnings" issues={preview.warnings} kind="warning" />
      {preview.ready === true ? <div className="vs-ops-approval"><label><input type="checkbox" checked={approved} onChange={(event) => setApproved(event.target.checked)} /><span><strong>I approve this exact dry run.</strong> Commit the selected CSV using {mode === "create" ? "Create New Only" : "Update Matching"}. Import success does not publish records.</span></label><button type="button" className="vs-primary" disabled={!approved || phase === "committing"} onClick={commit}>{phase === "committing" ? <LoaderCircle size={17} /> : <Check size={17} />} {phase === "committing" ? "Committing transaction" : "Approve and import"}</button></div> : null}
    </div> : null}

    {report ? <div className="vs-ops-panel is-report" role="status"><div className="vs-ops-step"><span><Check size={18} /></span><div><h3>Import reconciliation</h3><p>Import <code>{printable(report.importId)}</code> committed. Records remain normal editable Admin records and retain their imported publication state.</p></div></div><MetricGrid values={report.reconciliation || report.counts} /><IssueList title="Import warnings" issues={report.warnings} kind="warning" /><div className="vs-ops-destinations">{fileIndex + 1 < files.length ? <button type="button" className="vs-primary" onClick={advanceFile}>Continue to file {fileIndex + 2} of {files.length}</button> : null}<button type="button" onClick={onOpenStorefront}>Inspect Storefront Content</button><button type="button" onClick={onOpenSections}>Open Shop Sections</button><button type="button" onClick={onOpenProducts}>Open Products</button></div></div> : null}
  </section>;
}

function ownershipItem(item, index) {
  const product = item?.product && typeof item.product === "object" ? item.product : {};
  return {
    key: item?.id || item?.ownershipId || `${product.id || item?.productId || "owned"}-${index}`,
    name: product.name || item?.productName || item?.displayName || "Virtual Shop product",
    productId: product.id || item?.productId || item?.productIdentity,
    state: item?.state || item?.ownershipState || "owned",
    acquiredAt: item?.acquiredAt || item?.acquisitionTimestamp,
    creditsPaid: item?.shopCreditsPaid ?? item?.creditsPaid ?? item?.pricePaid,
    value: product.displayRetailValue || item?.displayRetailValue || item?.displayValueSnapshot,
    reference: item?.acquisitionReference || item?.sourceReference || item?.source,
  };
}

export function VirtualShopOwnershipSupport({ api }) {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function load(page = 1) {
    const customer = query.trim();
    if (!customer || loading) return;
    setLoading(true); setError("");
    const response = await api.read(READ.OWNERSHIP, { customer, page, pageSize: PAGE_SIZE });
    setLoading(false);
    if (!response.ok) { setResult(null); setError(response.message || "Ownership records could not be loaded."); return; }
    setResult(responseValue(response, ["ownership"], response.data || {}));
  }

  const customer = result?.customer || {};
  const items = (Array.isArray(result?.items) ? result.items : []).map(ownershipItem);
  const page = Number(result?.page) || 1;
  const total = Number(result?.total) || items.length;
  const pages = Math.max(1, Math.ceil(total / (Number(result?.pageSize) || PAGE_SIZE)));
  return <section className="vs-ops-screen" aria-label="Ownership Support">
    <header className="vs-ops-heading"><div><span className="vs-eyebrow">READ-ONLY CUSTOMER SUPPORT</span><h2>Ownership Support</h2><p>Inspect one authenticated customer account's Virtual Shop possessions. This screen cannot alter ownership or Shop Credits.</p></div><Package size={30} /></header>
    <form className="vs-ops-customer-search" onSubmit={(event) => { event.preventDefault(); load(1); }}><label><span>Customer ID or exact email</span><div><Search size={17} /><input value={query} maxLength={254} placeholder="Customer ID or email" onChange={(event) => { setQuery(event.target.value); setResult(null); setError(""); }} /></div></label><button className="vs-primary" type="submit" disabled={!query.trim() || loading}>{loading ? <LoaderCircle size={17} /> : <Search size={17} />} {loading ? "Loading" : "Find ownership"}</button></form>
    {error ? <div className="vs-ops-error" role="alert">{error}</div> : null}
    {result ? <div className="vs-ops-panel"><div className="vs-ops-customer"><div><span>Customer</span><h3>{printable(customer.displayName || customer.name || customer.email || customer.id)}</h3><p>{[customer.email, customer.id].filter(Boolean).join(" · ")}</p></div><strong>{total.toLocaleString()} owned {total === 1 ? "item" : "items"}</strong></div><MetricGrid values={result.summary} emptyText="No ownership summary was returned." />
      {items.length ? <div className="vs-ops-ownership-list"><div className="vs-ops-ownership-head"><span>Product</span><span>Acquired</span><span>Shop Credits paid</span><span>State / audit</span></div>{items.map((item) => <article key={item.key}><div><strong>{item.name}</strong><code>{printable(item.productId, "Identity unavailable")}</code>{item.value ? <small>Display value: {printable(item.value)}</small> : null}</div><time>{formatDate(item.acquiredAt)}</time><span>{printable(item.creditsPaid)}</span><div><strong>{displayKey(item.state)}</strong>{item.reference ? <code>{printable(item.reference)}</code> : null}</div></article>)}</div> : <div className="vs-ops-empty"><Package size={30} /><h3>No owned products</h3><p>This customer has no current Virtual Shop ownership records.</p></div>}
      {pages > 1 ? <nav className="vs-ops-pager" aria-label="Ownership pages"><button type="button" disabled={page <= 1 || loading} onClick={() => load(page - 1)}>Previous</button><span>Page {page} of {pages}</span><button type="button" disabled={page >= pages || loading} onClick={() => load(page + 1)}>Next</button></nav> : null}
    </div> : null}
  </section>;
}
